import React, { useState, useEffect } from "react";
import {
	useParams,
	Link,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import {
	Form,
	Input,
	Select,
	Row,
	Col,
	Space,
	Modal,
	Card,
	Divider,
	Typography,
	InputNumber,
	Switch,
	Tooltip,
} from "antd";
import {
	InnerForm,
	ProductData,
	PublishSettings,
	Container,
	CardContainer,
	Section,
	AntdModal,
	InfoContainer,
	InnerBlocks,
	FilterContainer,
	FilterActions,
} from "./styles";
import LoadingSkeleton from "./components/loading-skeleton/LoadingSkeleton";
import { CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import ProductCard from "./components/product-card/ProductCard";
import {
	get_laboratory_suggestion_by_id,
	run_MCDA_algoritm,
	update_laboratory_suggestion,
} from "../../../../../api/endpoints/laboratorySuggestions";
import { useAuth } from "../../../../../contexts/authContext";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import WeightMixer from "./components/weight-mixer/WeightMixes";
import AddProductsCuratedTable from "../../../../common/add-products-curated-table/AddProductsCuratedTable";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";
import SecondaryButton from "../../../../common/antd-buttons/SecondaryButton";
import TerciaryButton from "../../../../common/antd-buttons/TerciaryButton";
import AntdSelect from "../../../../common/antd-select/AntdSelect";

const { Option } = Select;
const GUTTER = 10;

export default function UpdateLaboratorySuggestion() {
	const { id } = useParams();
	const [form] = Form.useForm();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [initialValues, setInitialValues] = useState(null);
	const [show, setShow] = useState(null);
	const [showProductsModal, setShowProductsModal] = useState(false);
	const [curatedProducts, setCuratedProducts] = useState([]);
	const [weights, setWeights] = useState({
		weightOOS: 0.4,
		weightRecency: 0.25,
		weightFrequency: 0.2,
		weightLabCuration: 0.15,
		weightRecovery: 0.0,
	});

	useEffect(() => {
		const fetch_product_by_id = async () => {
			const data = await get_laboratory_suggestion_by_id(id);

			const suggestion = data.suggestions;
			setWeights({
				weightOOS: suggestion.weightOOS,
				weightRecency: suggestion.weightRecency,
				weightFrequency: suggestion.weightFrequency,
				weightLabCuration: suggestion.weightLabCuration,
				weightRecovery: suggestion.weightRecovery ?? 0,
			});

			setShow(data.offerTo && data.offerFrom);

			setCuratedProducts(
				data.curatedProducts?.map((i) => ({
					key: i.productId,
					id: i.productId,
					ean13: i.ean13,
					baseBoost: i.baseBoost,
					startDate: i.startDate,
					endDate: i.endDate,
					priority: i.priority,
					title: i.title,
					photoUrl: i.photoUrl,
					presentationName: i.presentationName,
				})),
			);

			setInitialValues({
				laboratoryName: data?.suggestions?.laboratoryName ?? "",
				laboratoryId: suggestion.laboratoryId,
				isEnabled: suggestion.isEnabled,
				lookbackDaysRecency: suggestion.lookbackDaysRecency,
				lookbackMonthsFrequency: suggestion.lookbackMonthsFrequency,
				lookbackMonthsRecovery: suggestion.lookbackMonthsRecovery,
				lookbackDaysOOS: suggestion.lookbackDaysOOS,
				maxSuggestionsPerPOS: suggestion.maxSuggestionsPerPOS,
				minOOSFirst: suggestion.minOOSFirst,
				forceLabCurated: suggestion.forceLabCurated,
				excludePausedProducts: suggestion.excludePausedProducts,
				excludeDraftProducts: suggestion.excludeDraftProducts,
				state: suggestion.isEnabled ? "ACTIVE" : "DELETED",
			});

			form.setFieldsValue({
				...suggestion,
				recoveryDaysTrigger: suggestion.recoveryDaysTrigger ?? 45,
				recoveryDaysFull: suggestion.recoveryDaysFull ?? 120,
				recoveryMinMonths: suggestion.recoveryMinMonths ?? 2,
				state: suggestion.isEnabled ? "ACTIVE" : "DELETED",
			});
		};

		if (id) {
			fetch_product_by_id();
		}
	}, []);

	const validateWeightsSum = () => {
		const sum =
			weights.weightOOS +
			weights.weightRecency +
			weights.weightFrequency +
			weights.weightLabCuration +
			weights.weightRecovery;
		return Math.abs(sum - 1) < 0.001;
	};

	const onFinish = async (values) => {
		if (!validateWeightsSum()) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Pesos inválidos",
				"La suma de los pesos debe ser exactamente 1.0",
			);
			return;
		}

		try {
			const curatedPayload = (curatedProducts ?? []).map((p) => ({
				laboratoryId: initialValues.laboratoryId,
				productId: p.id,
				ean13: p.ean13 || null,
				baseBoost: p.baseBoost,
				startDate: p.startDate || null,
				endDate: p.endDate || null,
				priority: p.priority ?? 0,
			}));

			setSubmitting(true);

			const labSuggestionPayload = {
				id,
				laboratoryId: initialValues.laboratoryId,
				isEnabled: values.state === "ACTIVE",

				weightOOS: weights.weightOOS,
				weightRecency: weights.weightRecency,
				weightFrequency: weights.weightFrequency,
				weightLabCuration: weights.weightLabCuration,
				weightRecovery: weights.weightRecovery,

				lookbackDaysRecency: Number(values.lookbackDaysRecency),
				lookbackMonthsFrequency: Number(values.lookbackMonthsFrequency),
				lookbackMonthsRecovery: Number(values.lookbackMonthsRecovery),
				lookbackDaysOOS: Number(values.lookbackDaysOOS),

				recoveryDaysTrigger: Number(values.recoveryDaysTrigger),
				recoveryDaysFull: Number(values.recoveryDaysFull),
				recoveryMinMonths: Number(values.recoveryMinMonths),

				maxSuggestionsPerPOS: Number(values.maxSuggestionsPerPOS),
				minOOSFirst: Number(values.minOOSFirst),
				forceLabCurated: !!values.forceLabCurated,

				excludePausedProducts: !!values.excludePausedProducts,
				excludeDraftProducts: !!values.excludeDraftProducts,
			};

			const payload = {
				id,
				laboratorySuggestion: labSuggestionPayload,
				laboratoryCuratedProducts: curatedPayload,
			};

			await update_laboratory_suggestion(id, payload);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Configuracion de sugeridos actualizada con éxito",
				"La configuracion de sugeridos se actualizó con éxito",
			);
			navigate(`/admin/laboratorySuggestions?uri=${uri}`);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al actualizar lo datos",
				"Un error ocurrio al intentar actualizar datos, contacte con soporte a soporte@checkpos.com.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	if (!id) navigate("/admin/laboratorySuggestions?uri=" + uri);

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido cargados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/laboratorySuggestions?uri=" + uri);
			},
		});
	};

	const handleExecuteAlgoritm = async () => {
		setSubmitting(true);
		try {
			await run_MCDA_algoritm({ laboratoryId: initialValues.laboratoryId });
			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Se ha ejecutado el algoritmo",
				"Listado de sugeridos actualizado con éxito",
			);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al ejecutar",
				"Un error ocurrio al ejecutar el algoritmo, contacte con soporte a soporte@checkpos.com.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/laboratorySuggestions?uri=" + uri}>
							Configuracion de sugeridos
						</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>
						Editar configuracion
					</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				{!initialValues ? (
					<LoadingSkeleton />
				) : (
					<Form
						form={form}
						name="lab-suggestions"
						onFinish={onFinish}
						autoComplete="off"
						requiredMark={false}
						layout="vertical"
						initialValues={initialValues}
					>
						<InnerBlocks>
							<InnerForm>
								<FilterContainer>
									<Form.Item
										label={
											<label style={{ color: COLORS.Primary, fontWeight: 600 }}>
												Laboratorio
											</label>
										}
										name="laboratoryName"
									>
										<Input
											placeholder="Nombre"
											disabled
											style={{
												borderRadius: 15,
												height: 32,
												width: 300,
												color: COLORS.MediumGrey,
											}}
										/>
									</Form.Item>
									<FilterActions>
										<Form.Item style={{ marginBottom: 0 }}>
											<PrimaryButton
												type="Primary"
												color="white"
												bg="Primary"
												htmlType="submit"
												loading={submitting}
												block
												style={{ height: 32, fontWeight: 400 }}
											>
												Guardar Cambios
											</PrimaryButton>
										</Form.Item>
										<SecondaryButton
											type="Primary"
											onClick={handleBack}
											loading={submitting}
											block
											style={{ height: 32, fontWeight: 400 }}
										>
											Volver sin guardar
										</SecondaryButton>
										<TerciaryButton
											type="Primary"
											onClick={handleExecuteAlgoritm}
											loading={submitting}
											block
											style={{ height: 32, fontWeight: 400 }}
										>
											Ejecutar algoritmo
										</TerciaryButton>
									</FilterActions>
								</FilterContainer>

								<PublishSettings>
									{/* === PESOS === */}
									<CardContainer>
										<Card
											title={
												<Typo level={5}>
													Pesos señales de interés{" "}
													<Tooltip
														title={`El puntaje final es una suma ponderada de cada peso que genera un valor que mide importancia de cada producto. Más peso en una señal = más impacto en el ranking.`}
														placement="topLeft"
														arrowPointAtCenter
													>
														<InfoCircleOutlined
															style={{
																color: COLORS.Secondary,
																paddingRight: 10,
															}}
														/>
													</Tooltip>
												</Typo>
											}
										>
											<WeightMixer value={weights} onChange={setWeights} />
											<Divider />
											<Row>
												<Col span={24}>
													<Typography.Text
														type={validateWeightsSum() ? "secondary" : "danger"}
													>
														Suma actual:{" "}
														{(
															weights.weightOOS +
															weights.weightRecency +
															weights.weightFrequency +
															weights.weightLabCuration +
															weights.weightRecovery
														).toFixed(2)}
													</Typography.Text>
												</Col>
											</Row>
										</Card>
									</CardContainer>
									{/* === PRODUCTOS DESTACADOS === */}
									<CardContainer>
										<Card
											title={
												<Typo level={5}>
													Productos destacados ({curatedProducts.length})
												</Typo>
											}
											extra={
												<SignInTemplate.AntdButton
													type="Primary"
													color="white"
													bg="Primary"
													onClick={() => setShowProductsModal(true)}
													loading={submitting}
													style={{ borderRadius: 20, fontWeight: 600 }}
												>
													Asignar productos
												</SignInTemplate.AntdButton>
											}
										>
											{!curatedProducts || curatedProducts.length === 0 ? (
												<Typography.Text type="secondary">
													Aún no hay productos asignados.
												</Typography.Text>
											) : (
												<Section>
													{curatedProducts.map((p, idx) => (
														<ProductCard key={idx} product={p} />
													))}
												</Section>
											)}
										</Card>

										<AntdModal
											visible={showProductsModal}
											onOk={() => setShowProductsModal(false)}
											onCancel={() => setShowProductsModal(false)}
											width={1200}
											centered
											footer={null}
											destroyOnClose
										>
											<InfoContainer>
												<AddProductsCuratedTable
													templateProducts={curatedProducts}
													setTemplateProducts={setCuratedProducts}
													userId={user.uid}
													laboratoryId={initialValues.laboratoryId}
													isMultiLab={false}
												/>
											</InfoContainer>
										</AntdModal>
									</CardContainer>
								</PublishSettings>
							</InnerForm>

							{/* === CONFIGURACIÓN DE CÁLCULO (4 columnas por tipo) === */}
							<CardContainer>
								<Card
									title={<Typo level={5}>Configuración de cálculo</Typo>}
									bodyStyle={{ paddingTop: 16, background: "#fff" }}
									headStyle={{ background: "#fff" }}
								>
									<Row gutter={GUTTER}>
										{/* Columna 1: Ventanas */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Ventanas"
												bordered={true}
												headStyle={{
													background: "#f8fafc",
													borderRadius: "12px 12px 0 0",
												}}
												bodyStyle={{ padding: 16 }}
											>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Ventana Faltantes (días)
														</Typo>
													}
													name="lookbackDaysOOS"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{
															width: "100%",
															borderRadius: 12,
														}}
													/>
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Ventana Recencia (días)
														</Typo>
													}
													name="lookbackDaysRecency"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Ventana Frecuencia (meses)
														</Typo>
													}
													name="lookbackMonthsFrequency"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Ventana Recupero (meses)
														</Typo>
													}
													name="lookbackMonthsRecovery"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
											</Card>
										</Col>

										{/* Columna 2: Recupero */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Recupero"
												bordered={true}
												headStyle={{
													background: "#f8fafc",
													borderRadius: "12px 12px 0 0",
												}}
												bodyStyle={{ padding: 16 }}
											>
												<Form.Item
													label={<Typo level={16}>Umbral inicio (días)</Typo>}
													name="recoveryDaysTrigger"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
												<Form.Item
													label={<Typo level={16}>Abandono pleno (días)</Typo>}
													name="recoveryDaysFull"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16}>Meses históricos mínimos</Typo>
													}
													name="recoveryMinMonths"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
											</Card>
										</Col>

										{/* Columna 3: Límites y priorización */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Límites y priorización"
												bordered={true}
												headStyle={{
													background: "#f8fafc",
													borderRadius: "12px 12px 0 0",
												}}
												bodyStyle={{ padding: 16 }}
											>
												<Form.Item
													label={
														<Typo level={16}>Máx. sugerencias por PDV</Typo>
													}
													name="maxSuggestionsPerPOS"
													rules={[{ required: true }]}
												>
													<InputNumber
														min={1}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16}>Mín. faltantes priorizados</Typo>
													}
													name="minOOSFirst"
												>
													<InputNumber
														min={0}
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>
												<Form.Item
													label={<Typo level={16}>Forzar destacados</Typo>}
													name="forceLabCurated"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
											</Card>
										</Col>

										{/* Columna 4: Filtrado y estado */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Filtrado y estado"
												bordered={true}
												headStyle={{
													background: "#f8fafc",
													borderRadius: "12px 12px 0 0",
												}}
												bodyStyle={{ padding: 16 }}
											>
												<Form.Item
													label="Excluir productos pausados"
													name="excludePausedProducts"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
												<Form.Item
													label="Excluir productos borrador"
													name="excludeDraftProducts"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
												<Form.Item
													label="Estado"
													name="state"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<AntdSelect allowClear placeholder="Estado">
														<Option value="ACTIVE">Activo</Option>
														<Option value="DELETED">Eliminado</Option>
													</AntdSelect>
												</Form.Item>
											</Card>
										</Col>
									</Row>
								</Card>
							</CardContainer>

							<AntdModal
								visible={showProductsModal}
								onOk={() => setShowProductsModal(false)}
								onCancel={() => setShowProductsModal(false)}
								width={1200}
								centered
								footer={null}
								destroyOnClose
							>
								<InfoContainer>
									<AddProductsCuratedTable
										templateProducts={curatedProducts}
										setTemplateProducts={setCuratedProducts}
										userId={user.uid}
										laboratoryId={initialValues.laboratoryId}
										isMultiLab={false}
									/>
								</InfoContainer>
							</AntdModal>
						</InnerBlocks>
					</Form>
				)}
			</Main.Body>
		</Main>
	);
}
